import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CameraAlt,
  AttachFile,
  Delete,
  Close,
  Save,
  CheckCircle,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import {
  ExpenseRequestDTO,
  ExpenseCategory,
  ExpenseStatus,
  ExpenseCategoryLabels,
  ExpenseStatusLabels,
  ExpenseResponseDTO,
} from '../../types/expense.types';
import expenseService from '../../services/expenseService';
import { Projet } from '../../types';
import { useAuthStore } from '../../stores/useAuthStore';

interface ExpenseFormProps {
  onSuccess?: (expense: ExpenseResponseDTO) => void;
  onCancel?: () => void;
  projects?: Projet[];
  initialData?: ExpenseResponseDTO;
  mode?: 'create' | 'edit';
}

interface ExpenseFormData {
  label: string;
  amountHt: number;
  amountTva: number;
  amountTtc: number;
  category: ExpenseCategory;
  date: string;
  status: ExpenseStatus;
  projetId: number | '';
}

/**
 * Formulaire de création/modification de dépense avec upload photo
 */
export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  onSuccess,
  onCancel,
  projects = [],
  initialData,
  mode = 'create',
}) => {
  const { user } = useAuthStore();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ExpenseFormData>({
    defaultValues: {
      label: initialData?.label || '',
      amountHt: initialData?.amountHt || 0,
      amountTva: initialData?.amountTva || 0,
      amountTtc: initialData?.amountTtc || 0,
      category: initialData?.category || ExpenseCategory.MATERIALS,
      date: initialData?.date || new Date().toISOString().split('T')[0],
      status: initialData?.status || ExpenseStatus.PENDING,
      projetId: initialData?.projetId || '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Watch amountHt and tauxTVA to auto-calculate
  const amountHt = watch('amountHt');
  const amountTva = watch('amountTva');

  // Auto-calculate TTC when HT or TVA changes
  useEffect(() => {
    if (amountHt >= 0 && amountTva >= 0) {
      const ttc = amountHt + amountTva;
      setValue('amountTtc', parseFloat(ttc.toFixed(2)));
    }
  }, [amountHt, amountTva, setValue]);

  // Calculer la TVA automatiquement (0% si exonéré, 20% sinon)
  const handleamountHtChange = (value: number) => {
    setValue('amountHt', value);
    // Si l'entreprise est exonérée de TVA, la TVA récupérable est 0
    const tva = user?.isExemptFromTVA ? 0 : value * 0.2;
    setValue('amountTva', parseFloat(tva.toFixed(2)));
  };

  // Arrêter le flux vidéo lors du démontage
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        expenseService.validateFile(file);
        setSelectedFile(file);
        setError(null);
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleCameraOpen = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Utiliser la caméra arrière sur mobile
        audio: false,
      });
      setStream(mediaStream);
      setShowCamera(true);

      // Attacher le flux à la vidéo
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (err: any) {
      setError(
        'Impossible d\'accéder à la caméra. Vérifiez les autorisations de votre navigateur.'
      );
      console.error('Erreur caméra:', err);
    }
  };

  const handleCameraCapture = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `recu_${Date.now()}.jpg`, { type: 'image/jpeg' });
            setSelectedFile(file);
            handleCameraClose();
          }
        }, 'image/jpeg', 0.9);
      }
    }
  };

  const handleCameraClose = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: ExpenseFormData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const expenseData: ExpenseRequestDTO = {
        label: data.label,
        amountHt: data.amountHt,
        amountTva: data.amountTva,
        amountTtc: data.amountTtc,
        category: data.category,
        date: data.date,
        status: data.status,
        projetId: data.projetId !== '' ? Number(data.projetId) : undefined,
      };

      // Créer ou mettre à jour la dépense
      let expense: ExpenseResponseDTO;
      if (mode === 'edit' && initialData?.id) {
        expense = await expenseService.update(initialData.id, expenseData);
      } else {
        expense = await expenseService.create(expenseData);
      }

      // Upload du justificatif si présent
      if (selectedFile && expense.id) {
        await expenseService.uploadReceipt(expense.id, selectedFile, {
          onProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(progress);
          },
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.(expense);
      }, 1000);
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde de la dépense:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Une erreur est survenue lors de la sauvegarde'
      );
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        {/* Alertes */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" icon={<CheckCircle />}>
            Dépense {mode === 'edit' ? 'modifiée' : 'créée'} avec succès !
          </Alert>
        )}

        {/* Alert franchise en base de TVA */}
        {user?.isExemptFromTVA && (
          <Alert severity="info">
            <strong>Franchise en base de TVA :</strong> Votre entreprise ne peut pas récupérer la TVA sur ses achats.
            Le montant de TVA récupérable est automatiquement fixé à 0€ pour toutes vos dépenses.
          </Alert>
        )}

        {/* Libellé */}
        <Controller
          name="label"
          control={control}
          rules={{ required: 'Le libellé est requis' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Libellé *"
              fullWidth
              error={!!errors.label}
              helperText={errors.label?.message}
              placeholder="Ex: Achat carrelage blanc 60x60"
            />
          )}
        />

        {/* Catégorie */}
        <Controller
          name="category"
          control={control}
          rules={{ required: 'La catégorie est requise' }}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.category}>
              <InputLabel>Catégorie *</InputLabel>
              <Select {...field} label="Catégorie *">
                {Object.values(ExpenseCategory).map((category) => (
                  <MenuItem key={category} value={category}>
                    {ExpenseCategoryLabels[category]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        {/* Montants */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            name="amountHt"
            control={control}
            rules={{
              required: 'Le montant HT est requis',
              min: { value: 0, message: 'Le montant doit être positif' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Montant HT *"
                type="number"
                fullWidth
                error={!!errors.amountHt}
                helperText={errors.amountHt?.message}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                onChange={(e) => handleamountHtChange(parseFloat(e.target.value) || 0)}
              />
            )}
          />

          <Controller
            name="amountTva"
            control={control}
            rules={{ min: { value: 0, message: 'Le montant doit être positif' } }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Montant TVA récupérable"
                type="number"
                fullWidth
                disabled={user?.isExemptFromTVA}
                error={!!errors.amountTva}
                helperText={
                  user?.isExemptFromTVA
                    ? 'Non récupérable (franchise en base)'
                    : errors.amountTva?.message || 'Calculé automatiquement (20%)'
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: user?.isExemptFromTVA ? '#f9fafb' : 'white',
                  },
                }}
              />
            )}
          />

          <Controller
            name="amountTtc"
            control={control}
            rules={{
              required: 'Le montant TTC est requis',
              min: { value: 0, message: 'Le montant doit être positif' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Montant TTC *"
                type="number"
                fullWidth
                error={!!errors.amountTtc}
                helperText={errors.amountTtc?.message || 'Calculé automatiquement'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  readOnly: true,
                }}
              />
            )}
          />
        </Stack>

        {/* Date et Statut */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            name="date"
            control={control}
            rules={{ required: 'La date est requise' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Date *"
                type="date"
                fullWidth
                error={!!errors.date}
                helperText={errors.date?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select {...field} label="Statut">
                  {Object.values(ExpenseStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {ExpenseStatusLabels[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Stack>

        {/* Projet */}
        {projects.length > 0 && (
          <Controller
            name="projetId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Projet (optionnel)</InputLabel>
                <Select {...field} label="Projet (optionnel)">
                  <MenuItem value="">
                    <em>Aucun projet</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.titre} - {project.adresseChantier}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        )}

        {/* Upload de justificatif */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Justificatif (optionnel)
          </Typography>

          {!selectedFile && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CameraAlt />}
                onClick={handleCameraOpen}
                fullWidth
              >
                Scanner le reçu
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
              >
                Choisir un fichier
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </Stack>
          )}

          {selectedFile && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={`${selectedFile.name} (${expenseService.formatFileSize(selectedFile.size)})`}
                onDelete={handleRemoveFile}
                deleteIcon={<Delete />}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}

          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Upload en cours: {uploadProgress}%
              </Typography>
              <CircularProgress variant="determinate" value={uploadProgress} size={20} />
            </Box>
          )}
        </Paper>

        {/* Boutons d'action */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {onCancel && (
            <Button onClick={onCancel} disabled={loading}>
              Annuler
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            disabled={loading || success}
          >
            {loading
              ? 'Enregistrement...'
              : mode === 'edit'
              ? 'Modifier'
              : 'Créer la dépense'}
          </Button>
        </Stack>
      </Stack>

      {/* Dialog Caméra */}
      <Dialog open={showCamera} onClose={handleCameraClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Scanner le reçu
          <IconButton
            onClick={handleCameraClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '75%', // Ratio 4:3
              backgroundColor: 'black',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCameraClose}>Annuler</Button>
          <Button variant="contained" startIcon={<CameraAlt />} onClick={handleCameraCapture}>
            Capturer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseForm;
